import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ArrowLeft, CalendarDays, Clock, MapPin, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { UserCreatedEvents } from '../../../../Data/event.dto'
import { useAppSelector } from '../../../../Redux/Hooks'
import { device } from '../../../atoms/Utlis/media'
import MarketplaceEmptyState from '../../../organisms/Dashboard/Marketplace/MarketplaceEmptyState'
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroRow,
  HeroLeft,
  BackButton,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  ScrollBody,
} from '../Member/memberFormStyles'

const gradient = 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

const SubEventPage: React.FC = () => {
  const [currentEvent, setCurrentEvent] = useState<UserCreatedEvents>()
  const { currentCreatedEvent } = useAppSelector((state) => state.event)
  const navigate = useNavigate()

  useEffect(() => {
    if (currentCreatedEvent) {
      setCurrentEvent(currentCreatedEvent)
    }
  }, [currentCreatedEvent])

  if (!currentCreatedEvent) return null

  const subEvents = currentEvent?.sub_events ?? []

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <div className="flex flex-col">
                <HeroBadge>
                  <Sparkles size={12} />
                  Event inquiry
                </HeroBadge>
                <PageTitle className="linear-text">
                  {currentEvent?.event_name || 'Event details'}
                </PageTitle>
                <PageSubtitle>
                  Schedule and location for each sub-event in this inquiry.
                </PageSubtitle>
              </div>
            </HeroText>
          </HeroLeft>
          {subEvents.length > 0 && (
            <CountPill>
              {subEvents.length} sub-event{subEvents.length !== 1 ? 's' : ''}
            </CountPill>
          )}
        </HeroRow>
      </Hero>

      <ScrollBody>
      <Panel>
        {subEvents.length === 0 ? (
          <MarketplaceEmptyState
            padding="48px 22px"
            icon={<CalendarDays size={36} strokeWidth={1.5} />}
            title="No sub-events"
            description="This inquiry does not include any sub-event schedule details yet."
            hint={{
              icon: <Sparkles size={13} />,
              text: 'Dates, duration and locations will appear here',
            }}
          />
        ) : (
          <CardGrid>
            {subEvents.map((event) => (
              <SubEventCard key={event.sub_event_name}>
                <CardTop>
                  <CardAccent />
                  <CardTitle>{event.sub_event_name}</CardTitle>
                </CardTop>

                <DetailBlock>
                  <DetailHeader>
                    <IconWrap $tone="#c94daa">
                      <CalendarDays size={16} />
                    </IconWrap>
                    <DetailLabel>Schedule</DetailLabel>
                  </DetailHeader>
                  <DetailGrid>
                    <DetailItem>
                      <MetaLabel>From</MetaLabel>
                      <MetaValue>
                        {event.starting_date} at {event.starting_time}
                      </MetaValue>
                    </DetailItem>
                    <DetailItem>
                      <MetaLabel>Duration</MetaLabel>
                      <MetaValue>
                        <Clock size={13} />
                        {event.duration} hours
                      </MetaValue>
                    </DetailItem>
                  </DetailGrid>
                </DetailBlock>

                <DetailBlock>
                  <DetailHeader>
                    <IconWrap $tone="#35b7c8">
                      <MapPin size={16} />
                    </IconWrap>
                    <DetailLabel>Location</DetailLabel>
                  </DetailHeader>
                  <LocationText>{event.sub_event_location || '—'}</LocationText>
                </DetailBlock>
              </SubEventCard>
            ))}
          </CardGrid>
        )}
      </Panel>
      </ScrollBody>
    </PageContainer>
  )
}

export default SubEventPage

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #c94daa;
`

const Panel = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  padding: 22px;
  box-sizing: border-box;
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 16px;
`

const SubEventCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(201, 77, 170, 0.1);
  }
`

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CardAccent = styled.div`
  width: 4px;
  height: 28px;
  border-radius: 999px;
  background: ${gradient};
  flex-shrink: 0;
`

const CardTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
  line-height: 1.25;
`

const DetailBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.04);
`

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconWrap = styled.div<{ $tone: string }>`
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`

const DetailLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const MetaLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
`

const MetaValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`

const LocationText = styled.p`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  line-height: 1.5;
`
